import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RiFlightTakeoffLine,
  RiFileTextLine,
  RiBankCardLine,
  RiNotification3Line,
} from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@components/ui/Drawer';
import { cn } from '@components/ui/utils';
import { apiClient } from '@/lib/api';
import { QuoteDetailsDrawer } from '@/features/quotes/components';
import FlightDetailDrawer from '@/features/track/components/FlightDetailDrawer';
import { getNotificationCopy, notificationBucket } from '../utils/notificationCopy';

/**
 * Filter pill definitions. `apiParam` is the comma-joined `category`
 * string the BE expects — the Financials pill unions payments AND
 * invoices because the broker mental-models them together even though
 * the BE splits the two.
 */
const FILTERS = [
  { key: 'all',        label: 'All',        apiParam: null },
  { key: 'flights',    label: 'Flights',    apiParam: 'flights' },
  { key: 'quotes',     label: 'Quotes',     apiParam: 'quotes' },
  { key: 'financials', label: 'Financials', apiParam: 'payments,invoices' },
];

/**
 * bucket (from notificationBucket) → leading icon on the card. Falls
 * through to a generic bell for unknown categories so future BE
 * additions don't render iconless.
 */
const ICON_FOR_BUCKET = {
  flight: RiFlightTakeoffLine,
  quote: RiFileTextLine,
  financial: RiBankCardLine,
};
const iconForBucket = (bucket) => ICON_FOR_BUCKET[bucket] || RiNotification3Line;

/**
 * Compact relative-time formatter matching the design's "N min ago" /
 * "N h ago" / "N d ago" copy. Kept inline — pulling a date-fns import
 * for three branches isn't worth the bundle cost.
 */
const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.max(0, Math.floor(diffMs / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} d ago`;
};

/**
 * Single notification card. Unread rows tint the whole row + swap the
 * icon chip to white; read rows stay plain.
 */
const NotificationCard = ({ event, onClick }) => {
  const bucket = notificationBucket(event);
  const Icon = iconForBucket(bucket);
  const isUnread = !event.is_read;
  const { title, subtitle } = getNotificationCopy(event);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 p-4 rounded-2xl border transition-colors text-left',
        'hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
        isUnread
          ? 'bg-primary-50/60 border-primary-100'
          : 'bg-white border-gray-100',
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center',
          isUnread ? 'bg-white' : 'bg-gray-100',
        )}
      >
        <Icon className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 leading-snug">{title}</p>
        <p className="mt-1 text-xs text-gray-500">
          {relativeTime(event.created_at)}
          {subtitle ? (
            <>
              <span className="mx-1.5">·</span>
              {subtitle}
            </>
          ) : null}
        </p>
      </div>
    </button>
  );
};

/**
 * Right-side drawer listing broker notifications (backed by the
 * /status-events feed). Filter pills scope the fetch by category;
 * clicking a card marks-as-read and routes:
 *   - flights → the /track flight-detail route with flightId + tail
 *   - anything else → the QuoteDetailsDrawer, opened inline with the
 *     event's quote_id (or the payment/invoice's parent quote_id)
 *
 * Mounted from AppLayout; opens via the sidebar's Notifications button.
 */
const NotificationDrawer = ({ isOpen, onClose }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const queryClient = useQueryClient();

  // Track the quote / flight the user drilled into. Kept in local
  // state so the NotificationDrawer stays open behind either detail
  // drawer — user can close it and continue skimming notifications.
  const [openQuoteId, setOpenQuoteId] = useState(null);
  const [openFlightId, setOpenFlightId] = useState(null);

  // Fetch the flight record on demand — FlightDetailDrawer takes a
  // fully-hydrated `flight` object rather than an id, so we resolve
  // it here before mounting.
  const { data: openFlight } = useQuery({
    queryKey: ['flightDetails', openFlightId],
    queryFn: () => apiClient.getFlightById(openFlightId),
    enabled: !!openFlightId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const activeFilterDef = FILTERS.find((f) => f.key === activeFilter) || FILTERS[0];
  const categoryParam = activeFilterDef.apiParam;

  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: ['statusEvents', categoryParam || 'all'],
    queryFn: () => apiClient.getStatusEvents({ skip: 0, limit: 50, category: categoryParam }),
    enabled: isOpen,          // only fetch while the drawer is mounted
    staleTime: 30 * 1000,     // 30 s — brokers reopen the drawer often
    refetchOnWindowFocus: false,
  });

  const markOneMutation = useMutation({
    mutationFn: (statusEventId) => apiClient.markStatusEventRead(statusEventId),
    // Optimistic — flip is_read locally so the tint clears immediately.
    onMutate: async (statusEventId) => {
      await queryClient.cancelQueries({ queryKey: ['statusEvents'] });
      queryClient.setQueriesData({ queryKey: ['statusEvents'] }, (old) =>
        Array.isArray(old)
          ? old.map((e) => (e.status_event_id === statusEventId ? { ...e, is_read: true } : e))
          : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['statusEvents'] });
      queryClient.invalidateQueries({ queryKey: ['statusEventsUnreadCount'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: () => apiClient.markAllStatusEventsRead(categoryParam),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['statusEvents'] });
      queryClient.setQueriesData({ queryKey: ['statusEvents', categoryParam || 'all'] }, (old) =>
        Array.isArray(old) ? old.map((e) => ({ ...e, is_read: true })) : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['statusEvents'] });
      queryClient.invalidateQueries({ queryKey: ['statusEventsUnreadCount'] });
    },
  });

  const list = useMemo(() => (Array.isArray(events) ? events : []), [events]);

  const handleCardClick = (event) => {
    // Fire-and-forget mark-as-read; optimistic update already flipped UI.
    if (!event.is_read) markOneMutation.mutate(event.status_event_id);

    if (event.category === 'flights') {
      // Open FlightDetailDrawer inline (same shell /track uses) so the
      // NotificationDrawer stays open behind it. FlightDetailDrawer
      // needs the full record, so we set flightId and let the useQuery
      // above resolve it before mounting.
      const flightId = event.flight_id || event.context?.flight?.flight_id;
      if (!flightId) return;
      setOpenFlightId(flightId);
      return;
    }

    // Non-flight → open the QuoteDetailsDrawer inline. Payment / invoice
    // rows carry their parent quote via context.payment.quote_id or
    // context.quote.quote_id, so we fall through both.
    const quoteId =
      event.quote_id
      || event.context?.quote?.quote_id
      || event.context?.payment?.quote_id
      || null;
    if (!quoteId) return;
    // No `status` — the status event's context doesn't carry it and the
    // drawer's own fetch fills it in. QuoteDetailsDrawer falls back to
    // quote.status when the prop is absent.
    setOpenQuoteId(quoteId);
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DrawerContent
          side="right"
          className="w-full !max-w-md !p-0 flex flex-col [&>button]:z-20"
        >
          {/* Header (title + close button live on the same row) */}
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm px-6 pt-5 pb-3 z-10">
            <DrawerHeader className="!space-y-0 !text-left mb-3">
              <DrawerTitle className="!text-xl font-semibold text-gray-900">
                Notifications
              </DrawerTitle>
            </DrawerHeader>

            {/* Filter pills + Read All action */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {FILTERS.map((f) => {
                  const isActive = activeFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setActiveFilter(f.key)}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                        isActive
                          ? 'bg-primary-700 text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50',
                      )}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending || list.every((e) => e.is_read)}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Read All
              </button>
            </div>
          </div>

          {/* Card list */}
          <div className="flex-1 overflow-y-auto px-4 pb-6 pt-2 space-y-3">
            {isLoading ? (
              <div className="mt-10 text-center text-sm text-gray-400">Loading…</div>
            ) : isError ? (
              <div className="mt-10 text-center text-sm text-red-500">
                Couldn't load notifications.
              </div>
            ) : list.length === 0 ? (
              <div className="mt-10 text-center text-sm text-gray-400">
                No notifications yet.
              </div>
            ) : (
              list.map((event) => (
                <NotificationCard
                  key={event.status_event_id}
                  event={event}
                  onClick={() => handleCardClick(event)}
                />
              ))
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Inline quote detail — mounts only after the user drills into
          a non-flight notification. Closing it doesn't close the
          NotificationDrawer, so brokers can continue scanning. */}
      <QuoteDetailsDrawer
        isOpen={!!openQuoteId}
        onOpenChange={(open) => { if (!open) setOpenQuoteId(null); }}
        quoteId={openQuoteId}
      />

      {/* Inline flight detail — mounts only after the flight fetch
          resolves so FlightDetailDrawer (which early-returns on !flight)
          doesn't flash empty. */}
      {openFlight && (
        <FlightDetailDrawer
          isOpen={!!openFlightId}
          onClose={() => setOpenFlightId(null)}
          flight={openFlight}
        />
      )}
    </>
  );
};

export default NotificationDrawer;
